/**
 * FlowDiagram — one flow graph drawn left to right: systems as nodes, handoffs
 * as arrows. Nodes carry the system name and time spent; the statuses behind a
 * system appear when it is selected. Routes are orthogonal so every label sits
 * on a horizontal run, and labels anchor to whichever run is unique to that
 * edge, keeping them apart on fan-out and fan-in. Geometry is computed, so
 * nothing is measured at runtime.
 */
import { useMemo } from 'react';
import { FlowGraph } from 'src/store/slices/paymentDetail/paymentDetailTypes';

const NODE_W = 140;
const NODE_H = 48;
const COLUMN_GAP = 150;
const ROW_GAP = 26;
const MARGIN = 8;
/** Spacing between the vertical runs of edges leaving the same node. */
const STAGGER = 14;

interface Box { x: number; y: number }
interface Route { id: string; path: string; labelX: number; labelY: number }

/** Counts how many times each id appears, to spot fan-out and fan-in. */
const tally = (ids: string[]): Map<string, number> => {
  const counts = new Map<string, number>();
  ids.forEach(id => counts.set(id, (counts.get(id) ?? 0) + 1));
  return counts;
};

/** Places columns left to right, centring each against the tallest. */
const place = (levels: string[][]): { boxes: Map<string, Box>; width: number; height: number } => {
  const columnHeight = (count: number) => count * NODE_H + Math.max(0, count - 1) * ROW_GAP;
  const content = Math.max(...levels.map(level => columnHeight(level.length)));
  const boxes = new Map<string, Box>();
  levels.forEach((level, column) => {
    const x = MARGIN + column * (NODE_W + COLUMN_GAP);
    let y = MARGIN + (content - columnHeight(level.length)) / 2;
    level.forEach(id => {
      boxes.set(id, { x, y });
      y += NODE_H + ROW_GAP;
    });
  });
  return {
    boxes,
    width: MARGIN * 2 + levels.length * NODE_W + (levels.length - 1) * COLUMN_GAP,
    height: MARGIN * 2 + content,
  };
};

/** Routes each edge out of its source, across, then into its target. */
const route = (graph: FlowGraph, boxes: Map<string, Box>): Route[] => {
  const drawable = graph.edges.filter(edge => boxes.has(edge.fromId) && boxes.has(edge.toId));
  const midY = (id: string) => boxes.get(id)!.y + NODE_H / 2;
  // An edge between two nodes on the same row is drawn straight across, so it
  // has no vertical run to spread and must not claim a stagger slot.
  const outgoing = tally(drawable
    .filter(edge => Math.abs(midY(edge.fromId) - midY(edge.toId)) >= 1)
    .map(edge => edge.fromId));
  const incoming = tally(drawable.map(edge => edge.toId));
  const seen = new Map<string, number>();

  return drawable.map(edge => {
    const from = boxes.get(edge.fromId)!;
    const to = boxes.get(edge.toId)!;
    const [x1, y1] = [from.x + NODE_W, from.y + NODE_H / 2];
    const [x2, y2] = [to.x, to.y + NODE_H / 2];

    if (Math.abs(y1 - y2) < 1) {
      return { id: edge.id, path: `M ${x1} ${y1} H ${x2}`, labelX: (x1 + x2) / 2, labelY: y1 };
    }

    // Turn inside the gap immediately before the target column, spreading the
    // vertical runs of edges that leave the same node. For adjacent columns
    // this is the edge midpoint; for an edge that skips a column — which
    // happens when the target also has a deeper parent — it keeps the vertical
    // run out of the intermediate node boxes the midpoint would cut through.
    // The clamp keeps the stagger inside the gap however wide the fan-out.
    const fanOut = outgoing.get(edge.fromId) ?? 1;
    const index = seen.get(edge.fromId) ?? 0;
    seen.set(edge.fromId, index + 1);
    const spread = fanOut > 1 ? (index - (fanOut - 1) / 2) * STAGGER : 0;
    const limit = COLUMN_GAP / 2 - STAGGER / 2;
    const turnX = x2 - COLUMN_GAP / 2 + Math.max(-limit, Math.min(limit, spread));

    // Label the run unique to this edge: the leaving run when several edges
    // arrive at the target, otherwise the arriving run.
    const onSource = (incoming.get(edge.toId) ?? 1) > 1;
    return {
      id: edge.id,
      path: `M ${x1} ${y1} H ${turnX} V ${y2} H ${x2}`,
      labelX: onSource ? (x1 + turnX) / 2 : (turnX + x2) / 2,
      labelY: onSource ? y1 : y2,
    };
  });
};

interface FlowDiagramProps {
  graph: FlowGraph;
  selectedNodeId?: string;
  onSelectNode: (nodeId: string) => void;
}

export const FlowDiagram = ({ graph, selectedNodeId, onSelectNode }: FlowDiagramProps) => {
  const { boxes, width, height } = useMemo(() => place(graph.levels), [graph.levels]);
  const routes = useMemo(() => route(graph, boxes), [graph, boxes]);
  const edgesById = useMemo(() => new Map(graph.edges.map(edge => [edge.id, edge])), [graph.edges]);

  if (graph.levels.length === 0) return null;
  const markerId = `flow-arrow-${graph.id}`;

  return (
    <svg
      className={`flow-diagram flow-diagram--${graph.direction.toLowerCase()}`}
      viewBox={`0 0 ${width} ${height}`}
      style={{ maxWidth: width }}
      role="group"
      aria-label={`${graph.title} flow diagram`}
    >
      <defs>
        <marker
          id={markerId}
          viewBox="0 0 10 10"
          refX="9"
          refY="5"
          markerWidth="7"
          markerHeight="7"
          orient="auto-start-reverse"
        >
          <path className="flow-diagram__arrowhead" d="M 0 0 L 10 5 L 0 10 z" />
        </marker>
      </defs>

      {routes.map(({ id, path, labelX, labelY }) => {
        const edge = edgesById.get(id);
        return (
          <g key={id} className="flow-diagram__edge">
            <path d={path} markerEnd={`url(#${markerId})`} />
            {edge?.label && (
              <text className="flow-diagram__edge-label" x={labelX} y={labelY - 7} textAnchor="middle">
                {edge.label}{edge.countLabel ? ` ${edge.countLabel}` : ''}
              </text>
            )}
            {edge?.latency && (
              <text className="flow-diagram__edge-meta" x={labelX} y={labelY + 13} textAnchor="middle">
                {edge.latency}
              </text>
            )}
          </g>
        );
      })}

      {graph.nodes.map(node => {
        const box = boxes.get(node.id);
        if (!box) return null;
        const selected = node.id === selectedNodeId;
        const meta = node.passThrough ? 'no reported phases' : node.latency;
        return (
          <g
            key={node.id}
            className={[
              'flow-diagram__node',
              `flow-diagram__node--${node.tone}`,
              node.bottleneck ? 'flow-diagram__node--bottleneck' : '',
              selected ? 'flow-diagram__node--selected' : '',
            ].filter(Boolean).join(' ')}
            role="button"
            tabIndex={0}
            aria-pressed={selected}
            aria-label={`${node.system}${node.latency ? `, ${node.latency}` : ''}`}
            onClick={() => onSelectNode(node.id)}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onSelectNode(node.id);
              }
            }}
          >
            <title>
              {[node.system, node.lastStatus, node.state, node.latency, node.bottleneck ? 'slowest' : undefined]
                .filter(Boolean).join(' · ')}
            </title>
            <rect
              className="flow-diagram__node-box"
              x={box.x}
              y={box.y}
              width={NODE_W}
              height={NODE_H}
              rx={8}
            />
            <text
              className="flow-diagram__node-name"
              x={box.x + NODE_W / 2}
              y={box.y + (meta ? 21 : 29)}
              textAnchor="middle"
            >
              {node.system}
            </text>
            {meta && (
              <text
                className="flow-diagram__node-meta"
                x={box.x + NODE_W / 2}
                y={box.y + 35}
                textAnchor="middle"
              >
                {meta}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
};
